/**
 * 蜜糖黄 · 阳光黄页 登录页
 *
 * 独立全屏分屏：左侧炭黑品牌面板（琥珀点缀），右侧奶油底登录卡。
 * 无站点守卫；登录成功后回跳 redirect 或会员中心。
 */

import { useState, useEffect, FormEvent } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { useSite } from "@/context/SiteContext";
import { useSiteConfig } from "@/context/SiteConfigContext";

const BRAND_POINTS = [
  { icon: "ri-price-tag-3-line", text: "海量分类 信息直达" },
  { icon: "ri-rocket-line", text: "一键发布 轻松管理" },
  { icon: "ri-shield-check-line", text: "诚信保障 安心交易" },
];

export default function HoneyLoginPage() {
  const { login } = useAuth();
  const { siteName } = useSite();
  const { siteConfig } = useSiteConfig();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [remember, setRemember] = useState(true);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [logo, setLogo] = useState("");
  const [logoError, setLogoError] = useState(false);

  const redirect = searchParams.get("redirect") || "";

  useEffect(() => {
    if (!siteConfig) return;
    if (siteConfig.logo) {
      setLogo(siteConfig.logo);
      setLogoError(false);
    }
  }, [siteConfig]);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError("");

    if (!username.trim() || !password) {
      setError("请输入账号与密码");
      return;
    }

    setLoading(true);
    const res = await login(username.trim(), password);
    setLoading(false);

    if (res.success) {
      if (remember) localStorage.setItem("remember_username", username.trim());
      else localStorage.removeItem("remember_username");
      navigate(redirect || "/dashboard", { replace: true });
    } else {
      setError(res.message || "登录失败，请稍后重试");
    }
  };

  return (
    <div className="min-h-screen bg-[#FFF9EC] lg:flex">
      {/* ═══════ 左侧品牌面板 ═══════ */}
      <div className="relative hidden lg:flex lg:w-[42%] xl:w-[45%] flex-col justify-between overflow-hidden bg-[#23201A] text-[#FFF9EC] px-12 py-12">
        <div className="pointer-events-none absolute -top-24 -right-24 w-96 h-96 rounded-full border-[30px] border-[#F0A500]/10" />
        <div className="pointer-events-none absolute -bottom-32 -left-24 w-80 h-80 rounded-full border-[24px] border-[#F0A500]/5" />
        <div className="pointer-events-none absolute top-1/3 right-10 w-6 h-6 rounded-full bg-[#F0A500]" />
        <div className="pointer-events-none absolute bottom-24 left-1/2 w-4 h-4 rounded-full bg-[#FFB520]" />

        <Link to="/" className="relative flex items-center gap-3 w-fit">
          <span className="shrink-0 inline-flex items-center justify-center w-11 h-11 rounded-xl overflow-hidden bg-[#F0A500] text-[#23201A]">
            {logo && !logoError ? (
              <img src={logo} alt={siteName} className="w-full h-full object-cover" onError={() => setLogoError(true)} />
            ) : (
              <span className="text-lg font-black">{(siteName || "S").charAt(0).toUpperCase()}</span>
            )}
          </span>
          <span>
            <span className="block text-lg font-black text-[#FFF9EC]">{siteName || "分类信息站"}</span>
            <span className="block text-[10px] text-[#8B8577] tracking-[0.25em] mt-0.5">CLASSIFIED</span>
          </span>
        </Link>

        <div className="relative">
          <p className="text-3xl font-black leading-snug tracking-tight">
            欢迎回到
            <br />
            <span className="text-[#F0A500]">阳光黄页</span>
          </p>
          <ul className="mt-8 space-y-4">
            {BRAND_POINTS.map((p) => (
              <li key={p.text} className="flex items-center gap-3 text-sm text-[#D8D1BF]">
                <span className="w-8 h-8 rounded-lg bg-white/5 border border-white/10 flex items-center justify-center text-[#F0A500]">
                  <i className={p.icon} />
                </span>
                {p.text}
              </li>
            ))}
          </ul>
        </div>

        <p className="relative text-xs text-[#8B8577]">© {new Date().getFullYear()} {siteName || "分类信息站"}</p>
      </div>

      {/* ═══════ 右侧登录区 ═══════ */}
      <div className="flex flex-col flex-1 px-4 sm:px-8 py-10 lg:py-0 justify-center">
        {/* 移动端品牌头 */}
        <div className="lg:hidden flex items-center justify-center gap-2.5 mb-8">
          <span className="shrink-0 inline-flex items-center justify-center w-9 h-9 rounded-lg overflow-hidden bg-[#F0A500] text-[#23201A]">
            {logo && !logoError ? (
              <img src={logo} alt="" className="w-full h-full object-cover" onError={() => setLogoError(true)} />
            ) : (
              <span className="text-sm font-black">{(siteName || "S").charAt(0).toUpperCase()}</span>
            )}
          </span>
          <span className="text-lg font-black text-[#23201A]">{siteName || "分类信息站"}</span>
        </div>

        <div className="w-full max-w-md mx-auto">
          <div className="bg-white rounded-2xl border border-[#EAE1CB] shadow-xl shadow-[#F0A500]/8 overflow-hidden">
            <div className="h-1.5 bg-gradient-to-r from-[#F0A500] via-[#FFBE30] to-[#F0A500]" />
            <div className="p-6 sm:p-8">
              <h1 className="text-2xl font-black text-[#23201A] tracking-tight">账号登录</h1>
              <p className="mt-1.5 text-xs text-[#8B8577]">欢迎回来，登录后管理您发布的信息</p>

              <form onSubmit={handleSubmit} className="mt-7 space-y-4">
                <div>
                  <label htmlFor="login-username" className="block text-[13px] font-bold text-[#4A453B] mb-1.5">
                    账号
                  </label>
                  <div className="flex items-center gap-2.5 bg-[#FFF9EC] border border-[#EAE1CB] rounded-xl px-4 py-3 focus-within:border-[#F0A500] focus-within:ring-2 focus-within:ring-[#F0A500]/20 transition-all">
                    <i className="ri-user-line text-[#B87A00]" />
                    <input
                      id="login-username"
                      type="text"
                      value={username}
                      onChange={(e) => setUsername(e.target.value)}
                      placeholder="请输入用户名/手机号/邮箱"
                      className="w-full bg-transparent outline-none text-sm text-[#23201A] placeholder-[#9A917E]"
                      autoComplete="username"
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="login-password" className="block text-[13px] font-bold text-[#4A453B] mb-1.5">
                    密码
                  </label>
                  <div className="flex items-center gap-2.5 bg-[#FFF9EC] border border-[#EAE1CB] rounded-xl px-4 py-3 focus-within:border-[#F0A500] focus-within:ring-2 focus-within:ring-[#F0A500]/20 transition-all">
                    <i className="ri-lock-line text-[#B87A00]" />
                    <input
                      id="login-password"
                      type={showPassword ? "text" : "password"}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="请输入密码"
                      className="w-full bg-transparent outline-none text-sm text-[#23201A] placeholder-[#9A917E]"
                      autoComplete="current-password"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="text-[#8B8577] hover:text-[#B87A00] transition-colors cursor-pointer"
                      aria-label={showPassword ? "隐藏密码" : "显示密码"}
                    >
                      <i className={`${showPassword ? "ri-eye-off-line" : "ri-eye-line"} text-base`} />
                    </button>
                  </div>
                </div>

                {error && (
                  <p className="flex items-start gap-1.5 text-xs text-[#B3261E] bg-[#FDE7E4] border border-[#F5C6C0] rounded-lg px-3 py-2.5">
                    <i className="ri-error-warning-line mt-0.5 shrink-0" />
                    {error}
                  </p>
                )}

                <div className="flex items-center justify-between">
                  <label className="flex items-center gap-2 text-xs text-[#6E6658] cursor-pointer select-none">
                    <input
                      type="checkbox"
                      checked={remember}
                      onChange={(e) => setRemember(e.target.checked)}
                      className="w-3.5 h-3.5 accent-[#F0A500]"
                    />
                    记住账号
                  </label>
                  <Link to="/forgot-password" className="text-xs text-[#B87A00] font-semibold hover:underline">
                    忘记密码？
                  </Link>
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full py-3.5 rounded-xl bg-[#F0A500] text-[#23201A] text-sm font-black hover:bg-[#FFB520] transition-colors shadow-lg shadow-[#F0A500]/25 disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2 cursor-pointer"
                >
                  {loading ? (
                    <>
                      <span className="w-4 h-4 border-2 border-[#23201A]/30 border-t-[#23201A] rounded-full animate-spin" />
                      登录中…
                    </>
                  ) : (
                    <>
                      <i className="ri-login-circle-line text-base" />登 录
                    </>
                  )}
                </button>
              </form>

              <div className="mt-6 pt-5 border-t border-dashed border-[#EAE1CB] text-center text-sm">
                <span className="text-[#6E6658]">还没有账号？</span>
                <Link to="/register" className="ml-1.5 text-[#B87A00] font-black hover:underline">
                  立即注册 <i className="ri-arrow-right-s-line align-middle" />
                </Link>
              </div>
            </div>
          </div>

          <p className="mt-6 text-center text-xs text-[#8B8577]">
            <Link to="/" className="hover:text-[#B87A00] transition-colors">
              <i className="ri-arrow-left-line align-middle mr-0.5" />返回首页
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
